import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Button,
  makeStyles,
  Grid,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from "@material-ui/core";
import { Select } from "@ofqual/portal-components/components/Select";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  ApplicationScopeStatus,
  defaultValues,
  generateTitle,
  getStatusNumberValue,
  getStatusStringValue,
  Scope,
} from "../../domain/Scope";
import {
  apprenticeshipStandardsService,
  inclusionExclusionService,
  levelsService,
  permissionTypesService,
  qualificationTypeService,
  sectorSubjectAreaService,
  technicalQualSubjectService,
} from "../../services";
import { Error } from "../common/Error";
import { v4 as uuidv4 } from "uuid";
import { LookupTrackerStore } from "@ofqual/portal-core/core/types";
import { getLookupTracker } from "@ofqual/portal-core/core/utils/modelHelpers";
import { fieldLabels } from "./fieldLabels";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";

const useStyles = makeStyles((theme) => ({
  dialogActions: {
    padding: 15,
  },
  body: {
    marginTop: 5,
    marginBottom: 5,
  },
}));

export type ScopeDialogProps = {
  scopes: Scope[];
  onClose: () => void;
  onAdd?: (scope: Scope) => void;
  onUpdate?: (scope: Scope, lookupTrackerInfo: LookupTrackerStore) => void;
  scopeToUpdate?: Scope;
  canApprove?: boolean;
  existingScopes?: Scope[];
  prevAddedScope?: Scope | null;
  allowSetScopeStart?: boolean;
};

export const ScopeDialog: React.FC<ScopeDialogProps> = ({
  onClose,
  scopes,
  onAdd,
  onUpdate,
  scopeToUpdate,
  canApprove,
  existingScopes,
  prevAddedScope,
  allowSetScopeStart
}) => {
  const classes = useStyles();
  const defaults = scopeToUpdate
    ? scopeToUpdate
    : !!prevAddedScope ? prevAddedScope : defaultValues;
  const { handleSubmit, control, errors, setValue, formState } = useForm<Scope>(
    {
      defaultValues: defaults,
    }
  );

  const [formError, setFormError] = useState<string | null>(null);

  const lookupTracker = useMemo(() => getLookupTracker(), []);
  const { dirtyFields } = formState;

  const lookupTrackingInfo = lookupTracker.raw();

  const validateAddScope = (
    scopeToAdd: Scope,
    newScopes: Scope[],
    existingScopes?: Scope[]
  ): string | undefined => {
    if (newScopes.filter((x) => x.title === scopeToAdd.title).length)
      return "This scope is already declared as a scope being applied for";
    else if (
      existingScopes &&
      existingScopes.filter((x) => x.title === scopeToAdd.title).length
    )
      return "This scope is already in your scope of recognition";
  };

  const handleAddItem = (data: Scope) => {
    setFormError(null);
    data.title = generateTitle(data, lookupTrackingInfo);
    const validateScope = validateAddScope(data, scopes, existingScopes);
    if (validateScope) {
      return setFormError(validateScope);
    }
    data.id = uuidv4();
    onAdd && onAdd(data);
    onClose();
  };

  const handleUpdateItem = (data: Scope) => {
    const updatedScope = { ...scopeToUpdate, ...data };
    setFormError(null);
    updatedScope.title = generateTitle(data, lookupTrackingInfo);
    const validateScope = validateAddScope(data, scopes, existingScopes);
    if (validateScope) {
      return setFormError(validateScope);
    }
    updatedScope.dirtyFields = Object.keys(dirtyFields);
    onUpdate && onUpdate(updatedScope, lookupTrackingInfo);
  };

  const hasScopeUpdated = useHasReduxObjectUpdated(scopeToUpdate);

  useEffect(() => {
    if (formState.isSubmitted && hasScopeUpdated) {
      onClose();
    }
  }, [hasScopeUpdated, formState.isSubmitted, onClose]);

  const [
    inclusionExclusionLookups,
    permissionTypesLookups,
    qualificationTypesLookups,
    levelsLookups,
    sectorSubjectAreaLookups,
    technicalQualSubjectLookups,
    apprenticeshipStandardsLookups,
  ] = useLookup(
    inclusionExclusionService,
    {
      service: permissionTypesService,
      id: "id",
      label: "description",
    },
    {
      service: qualificationTypeService,
      id: "id",
      label: "description",
    },
    {
      service: levelsService,
      id: "id",
      label: "levelValue",
    },
    sectorSubjectAreaService,
    {
      service: technicalQualSubjectService,
      id: "id",
      label: "description",
    },
    {
      service: apprenticeshipStandardsService,
      id: "id",
      label: "title",
    }
  );

  const {
    inclusionExclusion,
    recognitionPermissionType,
    qualificationType,
    status,
  } = useWatch({
    control,
    name: [
      "inclusionExclusion",
      "recognitionPermissionType",
      "qualificationType",
      "status",
    ],
  });

  const epaQualType = 25;
  const technicalQualType = 31;
  const descriptionPermissionType = 3;
  const generalConditionsPermissionType = 2;

  const isExcluded = inclusionExclusion === 0 ? true : false;

  const isExcludedOrNull = !inclusionExclusion || isExcluded;

  const isTechnicalQualRequired = qualificationType === technicalQualType;

  const isApprenticeshipStandardRequired = qualificationType === epaQualType;

  const isQualDescriptionRequired =
    isExcluded || recognitionPermissionType === descriptionPermissionType;

  const isQualTypeRequired =
    recognitionPermissionType && recognitionPermissionType === 1;

  const isLevelRequired =
    (qualificationType && qualificationType !== epaQualType) ||
    recognitionPermissionType === generalConditionsPermissionType;

  const isSsaRequired =
    (qualificationType &&
      qualificationType !== epaQualType &&
      qualificationType !== technicalQualType) ||
    recognitionPermissionType === generalConditionsPermissionType;

  const resetValues = (valuesToReset: any) => {
    valuesToReset.forEach((value: any) => setValue(value, null));
  };

  console.log(isQualDescriptionRequired);
  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-test="add-scope-dialog"
    >
      <DialogTitle data-test="add-scope-dialog-title">
        {onUpdate
          ? "Update a scope of recognition"
          : "Add a new scope of recognition"}
        <Divider />
      </DialogTitle>

      <DialogContent>
        <form>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="inclusionExclusion"
                defaultValue={null}
                rules={{ required: true }}
                render={(props) => {
                  const _onChange = (event: any) => {
                    // set the recognitionPermissionType
                    if (event.target.value === 0) {
                      setValue("recognitionPermissionType", 3);
                      resetValues([
                        "qualificationType",
                        "level",
                        "sectorSubjectArea",
                        "tLevelSubject",
                        "apprenticeshipStandard",
                        "qualificationDescription",
                      ]);
                    }
                    props.onChange(event);
                  };
                  return (
                    <Select
                      {...props}
                      label={fieldLabels.inclusionExclusion}
                      datatest="included-excluded"
                      onChange={_onChange}
                      lookupTracker={lookupTracker}
                      required
                    >
                      {inclusionExclusionLookups}
                    </Select>
                  );
                }}
              />
              {errors.inclusionExclusion && (
                <Error>This field is required</Error>
              )}
            </Grid>

            <Grid item xs={12}>
              <Controller
                control={control}
                name="recognitionPermissionType"
                defaultValue={null}
                rules={{ required: true }}
                render={(props) => {
                  const _onChange = (event: any) => {
                    // reset below fields
                    resetValues([
                      "qualificationType",
                      "level",
                      "sectorSubjectArea",
                      "tLevelSubject",
                      "apprenticeshipStandard",
                      "qualificationDescription",
                    ]);
                    props.onChange(event);
                  };
                  return (
                    <Select
                      {...props}
                      label={fieldLabels.recognitionPermissionType}
                      datatest="recognition-permission-type"
                      readOnly={isExcludedOrNull}
                      onChange={_onChange}
                      lookupTracker={lookupTracker}
                      required
                    >
                      {permissionTypesLookups}
                    </Select>
                  );
                }}
              />
              {errors.recognitionPermissionType && (
                <Error>This field is required</Error>
              )}
            </Grid>

            <Grid item xs={12}>
              <Controller
                control={control}
                name="qualificationType"
                defaultValue={null}
                rules={{ required: isQualTypeRequired }}
                render={(props) => {
                  const _onChange = (event: any) => {
                    // reset below fields
                    resetValues([
                      "level",
                      "sectorSubjectArea",
                      "tLevelSubject",
                      "apprenticeshipStandard",
                    ]);
                    props.onChange(event);
                  };
                  return (
                    <Select
                      {...props}
                      label={fieldLabels.qualificationType}
                      datatest="qualification-type"
                      readOnly={isExcludedOrNull || !isQualTypeRequired}
                      onChange={_onChange}
                      lookupTracker={lookupTracker}
                      required={isQualTypeRequired}
                    >
                      {qualificationTypesLookups}
                    </Select>
                  );
                }}
              />
              {errors.qualificationType && (
                <Error>This field is required</Error>
              )}
            </Grid>

            <Grid item xs={12}>
              <Controller
                control={control}
                name="level"
                defaultValue={null}
                rules={{ required: isLevelRequired }}
                as={
                  <Select
                    label={fieldLabels.level}
                    datatest="level"
                    readOnly={isExcludedOrNull || !isLevelRequired}
                    lookupTracker={lookupTracker}
                    required={isLevelRequired}
                  >
                    {levelsLookups}
                  </Select>
                }
              />
              {errors.level && <Error>This field is required</Error>}
            </Grid>

            <Grid item xs={12}>
              <Controller
                control={control}
                name="sectorSubjectArea"
                defaultValue={null}
                rules={{ required: isSsaRequired }}
                as={
                  <Select
                    label={fieldLabels.sectorSubjectArea}
                    datatest="ssa"
                    readOnly={isExcludedOrNull || !isSsaRequired}
                    lookupTracker={lookupTracker}
                    required={isSsaRequired}
                  >
                    {sectorSubjectAreaLookups}
                  </Select>
                }
              />
              {errors.sectorSubjectArea && (
                <Error>This field is required</Error>
              )}
            </Grid>

            <Grid item xs={12}>
              <Controller
                control={control}
                name="tLevelSubject"
                defaultValue={null}
                rules={{ required: isTechnicalQualRequired }}
                as={
                  <Select
                    label={fieldLabels.tLevelSubject}
                    datatest="t-level-subject"
                    readOnly={isExcludedOrNull || !isTechnicalQualRequired}
                    lookupTracker={lookupTracker}
                    required={isTechnicalQualRequired}
                  >
                    {technicalQualSubjectLookups}
                  </Select>
                }
              />
              {errors.tLevelSubject && <Error>This field is required</Error>}
            </Grid>

            <Grid item xs={12}>
              <Controller
                control={control}
                name="apprenticeshipStandard"
                defaultValue={null}
                rules={{ required: isApprenticeshipStandardRequired }}
                render={(props) => {
                  const _onChange = (event: any) => {
                    // set the level
                    const apprenticeshipStandard = apprenticeshipStandardsLookups.find(
                      (x) => x.id === event.target.value
                    );
                    setValue("level", apprenticeshipStandard?.levelId);

                    props.onChange(event);
                  };
                  return (
                    <Select
                      {...props}
                      label={fieldLabels.apprenticeshipStandard}
                      datatest="apprenticeship-standard"
                      readOnly={
                        isExcludedOrNull || !isApprenticeshipStandardRequired
                      }
                      onChange={_onChange}
                      lookupTracker={lookupTracker}
                      required={isApprenticeshipStandardRequired}
                    >
                      {apprenticeshipStandardsLookups}
                    </Select>
                  );
                }}
              />
              {errors.apprenticeshipStandard && (
                <Error>This field is required</Error>
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="qualificationDescription"
                defaultValue={null}
                rules={{ required: isQualDescriptionRequired }}
                as={
                  <TextField
                    fullWidth
                    label={fieldLabels.qualificationDescription}
                    readOnly={!isQualDescriptionRequired}
                    data-test="qual-description"
                    required={isQualDescriptionRequired}
                  />
                }
              />
              {errors.qualificationDescription && (
                <Error>This field is required</Error>
              )}
            </Grid>

            {canApprove ? (
              <>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="status"
                    render={({ value, name, onChange }) => {
                      return (
                        <FormControl component="fieldset">
                          <FormLabel component="legend">
                            Scope decision
                          </FormLabel>
                          <RadioGroup
                            aria-label="status"
                            name={name}
                            value={getStatusStringValue(value)}
                            data-test="svr-level-radio"
                            onChange={(e) =>
                              onChange(getStatusNumberValue(e.target.value))
                            }
                            row
                          >
                            <FormControlLabel
                              value="accepted"
                              control={<Radio />}
                              label={fieldLabels.accepted}
                              data-test="scope-accepted"
                            />
                            <FormControlLabel
                              value="rejected"
                              control={<Radio />}
                              label={fieldLabels.rejected}
                              data-test="scope-rejected"
                            />
                          </RadioGroup>
                        </FormControl>
                      );
                    }}
                  />
                </Grid>
              </>) : null}
              {(canApprove && status === ApplicationScopeStatus.Accepted) || allowSetScopeStart === true ? (
                <Grid item xs={6}>
                  <Controller
                    control={control}
                    rules={{ required: status === ApplicationScopeStatus.Accepted }}
                    name="validStartDate"
                    defaultValue={defaults.validStartDate || null}
                    as={
                      <DatePicker
                        dataTest="valid-start-date"
                        label={fieldLabels.validStartDate}
                        required={status === ApplicationScopeStatus.Accepted}
                      />
                    }
                  />
                  {errors.validStartDate && (
                    <Error>This field is required</Error>
                  )}
                </Grid>
              ) : null}
          </Grid>
        </form>
        {formError && <Error>{formError}</Error>}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={onClose}
          color="primary"
          data-test="close-add-scope-button"
        >
          Close
        </Button>
        {onAdd ? (
          <Button
            onClick={handleSubmit(handleAddItem)}
            color="primary"
            data-test="add-scope-button"
          >
            Add Scope
          </Button>
        ) : null}

        {onUpdate ? (
          <Button
            onClick={handleSubmit(handleUpdateItem)}
            color="primary"
            data-test="save-scope-button"
          >
            Confirm
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

const useHasReduxObjectUpdated = (object: any) => {
  const ref = useRef(object);
  return object !== ref.current;
};
